'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["messageId", "isGrouped", "slots", "slotProps", "children"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useMessage } from "../hooks/useMessage.js";
import { useChatVariant } from "../chat/internals/ChatVariantContext.js";
import { useChatDensity } from "../chat/internals/ChatDensityContext.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { MessageContextProvider } from "./internals/MessageContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
export const MessageRoot = /*#__PURE__*/React.forwardRef(function MessageRoot(props, ref) {
  const {
      messageId,
      isGrouped = false,
      slots,
      slotProps,
      children
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const message = useMessage(messageId);
  const variant = useChatVariant();
  const density = useChatDensity();
  const localeText = useChatLocaleText();
  const ownerState = React.useMemo(() => ({
    messageId,
    message,
    role: message?.role,
    status: message?.status,
    streaming: message?.status === 'streaming',
    error: message?.status === 'error',
    isGrouped,
    variant,
    density,
    showAvatar: message?.author?.avatarUrl != null
  }), [density, isGrouped, message, messageId, variant]);
  const Root = slots?.root ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref,
      // `article` is the correct landmark for a self-contained unit of content
      // (each chat message is a discrete piece of content in a feed).
      // Screen readers announce "article" when entering, which helps users
      // understand the structural boundary between messages.
      role: 'article',
      'aria-label': message?.author?.displayName ? `Message from ${message.author.displayName}` : localeText.messageLabel
    }, getDataAttributes({
      role: ownerState.role,
      status: ownerState.status,
      streaming: ownerState.streaming,
      error: ownerState.error,
      isGrouped: ownerState.isGrouped
    }))
  });
  return /*#__PURE__*/_jsx(MessageContextProvider, {
    value: ownerState,
    children: /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
      children: children
    }))
  });
});
if (process.env.NODE_ENV !== "production") MessageRoot.displayName = "MessageRoot";